<script setup lang="ts">
import LeButton from "@dsk/components/Button/LeButton.vue";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

import LeSuccessModal from "@/components/LeSuccessModal.vue";
import { useCompanyDictionaryAd } from "@/composables";
import { MatomoHelper } from "@/helpers";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

import LeCompanyDictionaryAdImage from "./LeCompanyDictionaryAdImage.vue";
import LeCompanyDictionaryAdLogo from "./LeCompanyDictionaryAdLogo.vue";
import LeCompanyDictionaryRequestModal from "./LeCompanyDictionaryRequestModal.vue";

const { t } = useI18n();
const { showAd } = useCompanyDictionaryAd();

const isSuccessModalOpen = ref<boolean>(false);
const isModalOpen = ref<boolean>(false);

const lemma = "firmaordbog";
const wordclass = "sb.";
const inflections = ["-en", "firmaordbøger", "firmaordbøgerne"];

const openModal = () => {
  isModalOpen.value = true;

  MatomoHelper.sendEvent(
    MatomoEventCategory.CompanyDictionaryAd,
    MatomoEventAction.ClickGetMoreInformation,
    "Bottom button in ad article was clicked"
  );
};

const closeModal = (didSubmit: boolean) => {
  isModalOpen.value = false;

  if (didSubmit) {
    isSuccessModalOpen.value = true;

    MatomoHelper.sendEvent(
      MatomoEventCategory.CompanyDictionaryAd,
      MatomoEventAction.FormSubmit,
      "Modal was submitted with information"
    );
  } else {
    MatomoHelper.sendEvent(
      MatomoEventCategory.CompanyDictionaryAd,
      MatomoEventAction.ClickClose,
      "Modal was closed without submitting"
    );
  }
};
</script>

<template>
  <article v-if="showAd" class="le-company-dictionary-ad">
    <div class="le-company-dictionary-ad__header">
      <div class="le-company-dictionary-ad__header-container">
        <span class="le-company-dictionary-ad__lemma">{{ lemma }}</span>
        <span class="le-company-dictionary-ad__wordclass">{{ wordclass }}</span>
        <span class="le-company-dictionary-ad__inflections">&lt; {{ inflections.join(", ") }} &gt;</span>
      </div>
    </div>
    <div class="le-company-dictionary-ad__section le-company-dictionary-ad__section--first">
      <span class="le-company-dictionary-ad__text">{{ t("le-company-dictionary-ad.article.text") }}</span>
    </div>
    <div class="le-company-dictionary-ad__section">
      <div class="le-company-dictionary-ad__body">
        <div>
          <p>
            <span
              class="le-company-dictionary-ad__text le-company-dictionary-ad__text--emphasize le-company-dictionary-ad__text--primary"
              >{{ t("le-company-dictionary-ad.article.example.label") }}</span
            >
            {{ t("le-company-dictionary-ad.article.example.category") }}
          </p>

          <ul class="le-company-dictionary-ad__list">
            <li>
              <span class="le-company-dictionary-ad__text le-company-dictionary-ad__text--emphasize">{{
                t("le-company-dictionary-ad.article.example.english.label")
              }}</span>
              {{ t("le-company-dictionary-ad.article.example.english.translation") }}
            </li>
            <li>
              <span class="le-company-dictionary-ad__text le-company-dictionary-ad__text--emphasize">{{
                t("le-company-dictionary-ad.article.example.danish.label")
              }}</span>
              {{ t("le-company-dictionary-ad.article.example.danish.translation") }}
            </li>
            <li>
              <span class="le-company-dictionary-ad__text le-company-dictionary-ad__text--emphasize">{{
                t("le-company-dictionary-ad.article.example.german.label")
              }}</span>
              {{ t("le-company-dictionary-ad.article.example.german.translation") }}
            </li>
          </ul>
        </div>
        <div>
          <LeCompanyDictionaryAdLogo />
        </div>
      </div>
    </div>
    <div class="le-company-dictionary-ad__section">
      <p
        class="le-company-dictionary-ad__text le-company-dictionary-ad__text--emphasize le-company-dictionary-ad__text--primary"
      >
        {{ t("le-company-dictionary-ad.article.list.title") }}
      </p>

      <div class="le-company-dictionary-ad__body">
        <div>
          <ul class="le-company-dictionary-ad__bullet-list">
            <li class="le-company-dictionary-ad__bullet">
              {{ t("le-company-dictionary-ad.article.list.items.translations") }}
            </li>
            <li class="le-company-dictionary-ad__bullet">
              {{ t("le-company-dictionary-ad.article.list.items.definitions") }}
            </li>
            <li class="le-company-dictionary-ad__bullet">
              {{ t("le-company-dictionary-ad.article.list.items.synonyms") }}
            </li>
            <li class="le-company-dictionary-ad__bullet">
              {{ t("le-company-dictionary-ad.article.list.items.abbreviations") }}
            </li>
            <li class="le-company-dictionary-ad__bullet">
              {{ t("le-company-dictionary-ad.article.list.items.classifications") }}
            </li>
            <li class="le-company-dictionary-ad__bullet">
              {{ t("le-company-dictionary-ad.article.list.items.subject") }}
            </li>
            <li class="le-company-dictionary-ad__bullet">
              {{ t("le-company-dictionary-ad.article.list.items.notes") }}
            </li>
            <li class="le-company-dictionary-ad__bullet">
              {{ t("le-company-dictionary-ad.article.list.items.references") }}
            </li>
          </ul>
        </div>

        <LeCompanyDictionaryAdImage class="le-company-dictionary-ad__image" />
      </div>
    </div>
    <div class="le-company-dictionary-ad__section le-company-dictionary-ad__section--last">
      <p
        class="le-company-dictionary-ad__text le-company-dictionary-ad__text--emphasize le-company-dictionary-ad__text--primary"
      >
        {{ t("le-company-dictionary-ad.article.terms.title") }}
      </p>

      <p>
        {{ t("le-company-dictionary-ad.article.terms.text") }}
      </p>

      <LeButton class="le-company-dictionary-ad__button" @click="openModal()">{{
        t("le-company-dictionary-ad.button")
      }}</LeButton>
    </div>

    <LeCompanyDictionaryRequestModal :is-open="isModalOpen" @close="closeModal($event)" />
    <LeSuccessModal
      :title-text="t('le-companydictionary.modal.title')"
      :body-text="t('le-companydictionary.modal.text.confirmation')"
      :confirm-text="t('le-companydictionary.modal.button.ok')"
      :is-open="isSuccessModalOpen"
      @close="isSuccessModalOpen = false"
    />
  </article>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";

.le-company-dictionary-ad {
  border-radius: 4px;
  background-color: @colors[@neutrals][100];
  box-shadow: @shadows[floating-default];

  &__header {
    display: flex;
    flex-direction: column;
    padding: 16px 30px;
    border-bottom: 2px solid @colors[@secondary][300];

    &-container {
      display: flex;
      align-items: center;
      gap: 16px;
      width: 100%;
    }
  }

  &__lemma {
    color: @colors[@primary][700];
    font-size: 1.625rem;
    font-weight: 700;
    line-height: 2rem;
    word-break: break-word;
    hyphens: auto;
  }

  &__wordclass,
  &__inflections {
    color: @colors[@neutrals][300];
  }

  &__section {
    padding: 24px 30px;
    border-bottom: 2px solid @colors[@secondary][300];
    color: @colors[@neutrals][500];
    font-size: 1rem;
    line-height: 1.375rem;

    &--first {
      padding-top: 30px;
    }

    &--last {
      padding-bottom: 30px;
      border-bottom: none;
    }
  }

  &__body {
    display: flex;
    justify-content: space-between;
    gap: 25px;
  }

  &__list {
    margin: 0;
    padding-left: 0;
    list-style: none;
  }

  &__bullet-list {
    margin: 0;
    padding-left: 12px;
    color: @colors[@secondary][800];
    font-size: 17px;
  }

  &__bullet {
    margin-left: 4px;
    padding-left: 8px;
    color: @colors[@neutrals][500];
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.375rem;

    &--last {
      margin-bottom: 0;
    }
  }

  &__image {
    align-self: center;
  }

  &__button {
    margin-top: 20px;
  }

  &__text {
    white-space: pre-line;

    &--emphasize {
      font-weight: 700;
    }

    &--primary {
      color: @colors[@primary][700];
    }
  }
}
</style>
